import assertNever from "assert-never";
import React from "react";
import {
  OnboardingVisitInstanceId,
  PublicOnboardingVisitDetails,
  PublicVisitBroadcastDetails,
  VisitInstanceId,
} from "../messages";
import { VisitType } from "../system/AppState/AppVisitsState";
import { HashSet } from "../utils/HashSet";
import AvailableFlexibleDates from "./AvailableFlexibleDates";
import AvailableSinglesDates from "./AvailableSinglesDates";
import { CheckedVisitData } from "./AvailableVisitActivity";
import AvailableWeeklyTemplateDates from "./AvailableWeeklyTemplateDates";
import { CheckedOnboardingVisitData } from "./OnboardingVisitActivity";
import { FlexibleVisitProps } from "./useFlexibleVisit";

interface PropsTypes {
  Onboarding: {
    checkedVisitData: CheckedOnboardingVisitData;
    visitInstanceId: OnboardingVisitInstanceId;
  };
  Available: {
    checkedVisitData: CheckedVisitData;
    visitInstanceId: VisitInstanceId;
  };
}

interface Props<TVisitType extends VisitType> {
  allowMultipleCaregivers: boolean;
  broadcastDetails: PublicVisitBroadcastDetails | PublicOnboardingVisitDetails;
  checkedVisits: HashSet<PropsTypes[VisitType]["visitInstanceId"]>;
  checkedPatterns: HashSet<string>;
  requestedVisitBatch: boolean;
  areAllShiftsChecked: boolean;
  visitType: TVisitType;
  flexibleVisitProps: FlexibleVisitProps | null;
  showSelectAll: boolean;
  choiceCounterHeaderTitle: string | null;
  onCheckVisit: (visitData: PropsTypes[VisitType]["checkedVisitData"]) => void;
}

const AvailableVisitDates = <TVisitType extends VisitType>(props: Props<TVisitType>) => {
  switch (props.broadcastDetails.type) {
    case "Pattern":
      return (
        <AvailableWeeklyTemplateDates
          allowMultipleCaregivers={props.allowMultipleCaregivers}
          pattern={props.broadcastDetails}
          onCheckVisit={props.onCheckVisit}
          checkedPatterns={props.checkedPatterns}
          requestedVisitBatch={props.requestedVisitBatch}
          areAllShiftsChecked={props.areAllShiftsChecked}
        />
      );
    case "Singles":
      return (
        <AvailableSinglesDates
          allowMultipleCaregivers={props.allowMultipleCaregivers}
          dates={props.broadcastDetails.visitInstances}
          checkedVisits={props.checkedVisits}
          requestedVisitBatch={props.requestedVisitBatch}
          areAllShiftsChecked={props.areAllShiftsChecked}
          visitType={props.visitType}
          onCheckVisit={props.onCheckVisit}
        />
      );
    case "FlexiblePattern":
      if (props.flexibleVisitProps === null) {
        throw new Error("flexibleVisitProps is x");
      }

      return (
        <AvailableFlexibleDates
          visit={props.broadcastDetails}
          flexibleVisitProps={props.flexibleVisitProps}
          showSelectAll={props.showSelectAll}
          choiceCounterHeaderTitle={props.choiceCounterHeaderTitle}
        />
      );
    default:
      assertNever(props.broadcastDetails);
  }
};

export default AvailableVisitDates;
